import { Link } from "react-router-dom";
import PageMeta from "@/components/base/PageMeta";
import JsonLd from "@/components/base/JsonLd";
import PageHero from "@/components/feature/PageHero";
import SiteLayout from "@/components/feature/SiteLayout";
import ContactForm from "@/pages/contact/components/ContactForm";
import { siteInfo } from "@/mocks/site";

const channels = [
  {
    icon: "ri-mail-line",
    title: "邮箱联系",
    value: siteInfo.email,
    desc: "内容纠错、线索投稿与合作咨询均可发送至该邮箱。",
  },
  {
    icon: "ri-time-line",
    title: "回复时效",
    value: siteInfo.feedbackTime,
    desc: "工作日提交的留言通常会更快得到回复。",
  },
  {
    icon: "ri-calendar-schedule-line",
    title: "更新节奏",
    value: siteInfo.workTime,
    desc: "合集与榜单按固定节奏更新，节假日照常。",
  },
];

const contactSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: `联系${siteInfo.name}`,
  description: `${siteInfo.name}的联系方式与留言入口，可提交内容纠错、选题建议、线索投稿与合作咨询。`,
  inLanguage: "zh-CN",
  url: "https://dearyuan.cn/contact",
  mainEntity: {
    "@type": "Organization",
    name: siteInfo.name,
    email: siteInfo.email,
  },
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "首页", item: "https://dearyuan.cn/" },
    { "@type": "ListItem", position: 2, name: "联系我们" },
  ],
};

export default function Contact() {
  return (
    <>
      <PageMeta
        title="联系我们｜瓜田日报内容纠错与线索投稿"
        description="欢迎联系瓜田日报：提交内容纠错、选题建议、线索投稿或合作咨询。填写留言表单即可，编辑团队会在一个工作日内回复你的邮箱。"
        keywords="联系瓜田日报,内容纠错,线索投稿,爆料吃瓜投稿,合作咨询"
        canonical="/contact"
      />
      <JsonLd data={contactSchema} />
      <JsonLd data={breadcrumbSchema} />

      <SiteLayout>
        <PageHero
          eyebrow="联系我们"
          title="发现问题、想补充信息，都可以直接说"
          desc="我们最欢迎两类留言：一是内容有出入的纠错，二是能提供公开来源的线索。两种都会优先处理。"
          crumbs={[{ label: "联系我们" }]}
        />

        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            {channels.map((item) => (
              <div
                key={item.title}
                className="rounded-card border border-background-200/80 bg-background-50 p-5"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary-100 text-secondary-700">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <h2 className="mt-4 text-sm font-bold text-foreground-900">{item.title}</h2>
                <p className="mt-1.5 break-all text-sm font-medium text-primary-600">{item.value}</p>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px]">
            <ContactForm />

            <aside className="flex flex-col gap-6">
              <div className="rounded-card border border-background-200/80 bg-accent-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-lightbulb-line text-accent-700"></i>
                  </span>
                  这样写，处理更快
                </h2>
                <ul className="mt-4 flex flex-col gap-3 text-xs leading-relaxed text-foreground-600">
                  <li className="flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500"></span>
                    纠错请附上话题标题与联系方式截图，说明哪一处表述需要修正。
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500"></span>
                    投稿请提供可公开追溯的来源，我们会先做事实核查。
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500"></span>
                    不接收涉及个人隐私、未经同意的私人影像等类型的内容。
                  </li>
                </ul>
              </div>

              <div className="rounded-card border border-background-200/80 bg-background-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-questionnaire-line text-primary-500"></i>
                  </span>
                  先看看常见问题
                </h2>
                <p className="mt-3 text-xs leading-relaxed text-foreground-600">
                  关于更新节奏、榜单算法和内容原则，我们已经在常见问题里做了说明，可能比等待回复更快。
                </p>
                <div className="mt-4 flex flex-col gap-2">
                  <Link
                    to="/about#faq"
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 transition-colors duration-200 hover:text-primary-700"
                  >
                    查看关于我们的常见问题
                    <span className="flex h-3.5 w-3.5 items-center justify-center">
                      <i className="ri-arrow-right-line text-[13px]"></i>
                    </span>
                  </Link>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 transition-colors duration-200 hover:text-primary-700"
                  >
                    回到首页的常见问题
                    <span className="flex h-3.5 w-3.5 items-center justify-center">
                      <i className="ri-arrow-right-line text-[13px]"></i>
                    </span>
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </SiteLayout>
    </>
  );
}